// PublicDataTab — issuer public-data lookup results (EDGAR, OTC Markets,
// Yahoo, FINRA, OFAC, SEC suspensions, promo/SearXNG, ...). Ported from
// the legacy CaseDetail.tsx public-data tab, adapted to the DataWash
// pane layout.
//
// Self-contained: loads the case's cached public data + history on mount,
// exposes a Refresh Data button (triggers a background lookup + polls),
// per-source refresh, and a search-history expander. The parent can bump
// `refreshNonce` to trigger a refresh from outside (e.g. the Overview
// "Pull Public Data" button).

import { useCallback, useEffect, useState } from 'react';
import {
  RefreshCw, Loader2, ExternalLink, History, AlertCircle,
} from 'lucide-react';
import { useToastStore } from '../stores/toast';
import {
  listPublicData, getPublicDataHistory, triggerPublicLookup,
  getPublicDataRun, getLatestPublicDataRun,
  type PublicDataResult,
} from '../api/review';
import type { Case } from '../api/cases';

interface PublicDataTabProps {
  caseData: Case;
  /** Bumped by the parent to request a refresh from outside (0 = no op). */
  refreshNonce?: number;
}

const POLL_INTERVAL = 4000;

// Fields that carry monetary values (render with $ + scale).
const MONETARY_FIELDS = new Set([
  'revenue', 'total_assets', 'goodwill', 'goodwill_intangibles',
  'market_cap', 'consideration_paid', 'share_price', 'assets',
  'last_price', 'previous_close', 'bid_price', 'ask_price',
]);

function formatMoney(n: number): string {
  if (Math.abs(n) >= 1_000_000_000) return '$' + (n / 1_000_000_000).toFixed(2) + 'B';
  if (Math.abs(n) >= 1_000_000) return '$' + (n / 1_000_000).toFixed(2) + 'M';
  if (Math.abs(n) >= 1_000) return '$' + n.toLocaleString();
  return '$' + String(n);
}

function formatNumber(n: number): string { return n.toLocaleString(); }

function prettyKey(key: string): string {
  return key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function renderMaybeUrl(s: string): React.ReactNode {
  if (s && /^https?:\/\//i.test(s)) {
    return (
      <a href={s} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline break-words">
        {s}
      </a>
    );
  }
  return s;
}

function renderValue(v: unknown, fieldName?: string): React.ReactNode {
  if (v === null || v === undefined) return <span className="text-slate-300">-</span>;
  if (typeof v === 'string') return renderMaybeUrl(v);
  if (typeof v === 'number') {
    if (fieldName && MONETARY_FIELDS.has(fieldName.toLowerCase())) return <span>{formatMoney(v)}</span>;
    if (Number.isInteger(v) && Math.abs(v) >= 1000) return <span>{formatNumber(v)}</span>;
    return <span>{String(v)}</span>;
  }
  if (typeof v === 'boolean') return <span>{String(v)}</span>;
  if (Array.isArray(v)) {
    if (v.length === 0) return <span className="text-slate-400">empty</span>;
    // EDGAR recent filings list: each item has url + form.
    if (v.length > 0 && typeof v[0] === 'object' && v[0] !== null &&
        'url' in (v[0] as Record<string, unknown>) && 'form' in (v[0] as Record<string, unknown>)) {
      return (
        <div className="space-y-1">
          {v.map((item, i) => {
            const f = item as Record<string, unknown>;
            const url = String(f.url || '');
            const label = `${String(f.form || '')}${f.date ? ' (' + f.date + ')' : ''}`;
            return (
              <div key={i} className="text-xs break-words">
                {url ? (
                  <a href={url} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                    {label || url}
                  </a>
                ) : (
                  <span className="text-slate-700">{label}</span>
                )}
              </div>
            );
          })}
        </div>
      );
    }
    return (
      <div className="space-y-1">
        {v.map((item, i) => (
          <div key={i} className="text-xs text-slate-700 break-words">{renderValue(item, fieldName)}</div>
        ))}
      </div>
    );
  }
  if (typeof v === 'object') {
    const obj = v as Record<string, unknown>;
    return (
      <div className="space-y-0.5">
        {Object.keys(obj).map((k) => (
          <div key={k} className="flex gap-2 text-xs">
            <span className="font-medium text-slate-500 shrink-0">{prettyKey(k)}:</span>
            <span className="text-slate-700 break-words">{renderValue(obj[k], k)}</span>
          </div>
        ))}
      </div>
    );
  }
  return <span>{String(v)}</span>;
}

interface FormattedItem { key: string; value: unknown; source: string }

function formatPublicData(data: Record<string, unknown> | null | undefined): FormattedItem[] {
  if (!data || typeof data !== 'object') return [];
  const items: FormattedItem[] = [];
  const SKIP = new Set(['source_url', 'retrieved_at', 'screenshot_path', 'pdf_path']);
  for (const [k, v] of Object.entries(data)) {
    if (v === null || v === undefined) continue;
    if (SKIP.has(k)) continue;
    if (typeof v === 'object' && 'value' in v) {
      const val = (v as Record<string, unknown>).value;
      const src = (v as Record<string, unknown>).source_url as string || '';
      if (val !== null && val !== undefined) items.push({ key: k, value: val, source: src });
    } else {
      items.push({ key: k, value: v, source: '' });
    }
  }
  return items;
}

export default function PublicDataTab({ caseData, refreshNonce }: PublicDataTabProps) {
  const { addToast } = useToastStore();
  const [publicData, setPublicData] = useState<PublicDataResult[]>([]);
  const [history, setHistory] = useState<Array<{ timestamp: string; source_count: number; sources: string[]; result_count: number; items: PublicDataResult[] }>>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [pollingRunId, setPollingRunId] = useState<string | null>(null);
  const [refreshingSource, setRefreshingSource] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [pd, hist] = await Promise.all([
        listPublicData(caseData.id),
        getPublicDataHistory(caseData.id).catch(() => ({ history: [] } as any)),
      ]);
      setPublicData(pd.items || []);
      setHistory(hist.history || []);
      // Detect an in-flight refresh so the button stays disabled.
      try {
        const run = await getLatestPublicDataRun(caseData.id);
        if (run.status === 'RUNNING' && run.run_id) {
          setRefreshing(true);
          setPollingRunId(run.run_id);
        }
      } catch { /* non-fatal */ }
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to load public data');
    } finally {
      setLoading(false);
    }
  }, [caseData.id]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Poll an in-flight public-data refresh run.
  useEffect(() => {
    if (!caseData.id || !pollingRunId) return;
    let cancelled = false;
    let done = false;
    const poll = async () => {
      if (done || cancelled) return;
      let run;
      try { run = await getPublicDataRun(caseData.id, pollingRunId); }
      catch { return; }
      if (done || cancelled) return;
      if (run.status === 'COMPLETED') {
        done = true;
        setPollingRunId(null);
        setRefreshing(false);
        setRefreshingSource(null);
        await loadAll();
        addToast('Public data refreshed', 'success');
      } else if (run.status === 'FAILED') {
        done = true;
        setPollingRunId(null);
        setRefreshing(false);
        setRefreshingSource(null);
        addToast('Public data refresh failed' + (run.error_message ? ': ' + run.error_message : ''), 'error');
      }
    };
    poll();
    const h = setInterval(poll, POLL_INTERVAL);
    return () => { cancelled = true; clearInterval(h); };
  }, [pollingRunId, caseData.id, addToast, loadAll]);

  // External refresh trigger (from the Overview "Pull Public Data" button).
  useEffect(() => {
    if (refreshNonce && refreshNonce > 0) doRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshNonce]);

  const doRefresh = async (sources?: string[]) => {
    const queryValue = caseData.ticker || caseData.issuer_name || caseData.entity_name || '';
    if (!queryValue) {
      addToast('No issuer name or ticker to look up', 'error');
      return;
    }
    setRefreshing(true);
    setError(null);
    try {
      const resp = await triggerPublicLookup(
        caseData.id,
        caseData.ticker ? 'ticker' : 'name',
        queryValue,
        sources,
      );
      addToast(sources && sources.length ? `${sources[0]} refresh started` : 'Public data refresh started', 'success');
      setPollingRunId(resp.run_id);
    } catch (e: any) {
      setRefreshing(false);
      if (e?.response?.status === 409) addToast('A refresh or review is already in progress', 'error');
      else addToast('Failed to refresh public data', 'error');
    }
  };

  // Group results by source (latest result per query_type per source).
  const grouped = (() => {
    const visible = publicData.filter((p) => p.source !== 'cpds');
    if (visible.length === 0) return [];
    const bySource: Record<string, { source: string; items: PublicDataResult[]; source_url: string | null; retrieved_at: string }> = {};
    const seen: Record<string, Set<string>> = {};
    const sorted = [...visible].sort((a, b) => (b.retrieved_at || '').localeCompare(a.retrieved_at || ''));
    for (const p of sorted) {
      if (!bySource[p.source]) {
        bySource[p.source] = { source: p.source, items: [], source_url: null, retrieved_at: p.retrieved_at };
        seen[p.source] = new Set();
      }
      const key = `${p.source}:${p.query_type}`;
      if (seen[p.source].has(key)) continue;
      seen[p.source].add(key);
      bySource[p.source].items.push(p);
      if (p.source_url && !bySource[p.source].source_url) bySource[p.source].source_url = p.source_url;
    }
    return Object.values(bySource).sort((a, b) => a.source.localeCompare(b.source));
  })();

  return (
    <div className="p-4 space-y-4" data-testid="public-data-tab">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-800">Public Data</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Issuer information fetched from EDGAR, OTC Markets, Yahoo, FINRA, OFAC, and other sources.
          </p>
        </div>
        <button
          onClick={() => doRefresh()}
          disabled={refreshing}
          className="btn-primary inline-flex items-center gap-2 text-sm disabled:opacity-50"
          data-testid="public-data-refresh"
        >
          {refreshing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
          Refresh Data
        </button>
      </div>

      {error && (
        <div className="text-sm text-red-600 flex items-center gap-2">
          <AlertCircle size={14} /> {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400">
          <Loader2 size={16} className="animate-spin" /> Loading public data...
        </div>
      ) : grouped.length === 0 ? (
        <div className="text-center text-slate-400 py-12 text-sm">
          No public data yet. Click <strong>Refresh Data</strong> to fetch issuer information from the internet.
        </div>
      ) : (
        <div className="space-y-3">
          {grouped.map((grp) => {
            const dataPoints = grp.items.filter((pd) => pd.query_type !== 'screenshot_path' && pd.query_type !== 'pdf_path');
            return (
              <div key={grp.source} className="p-4 rounded-lg border border-slate-200 bg-slate-50">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-slate-700 uppercase">{grp.source}</span>
                    <span className="text-xs px-2 py-0.5 rounded bg-slate-200 text-slate-600">{dataPoints.length} data points</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400">{new Date(grp.retrieved_at).toLocaleString()}</span>
                    <button
                      onClick={() => doRefresh([grp.source])}
                      disabled={refreshing}
                      className="text-xs text-primary-600 hover:text-primary-700 inline-flex items-center gap-1 disabled:opacity-50"
                    >
                      {refreshingSource === grp.source ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
                      Refresh
                    </button>
                  </div>
                </div>
                {grp.source_url && (
                  <div className="mb-2">
                    <a href={grp.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-primary-600 hover:underline">
                      <ExternalLink size={12} /> View Source Page
                    </a>
                  </div>
                )}
                <div className="bg-white rounded-lg p-3 border border-slate-100">
                  {dataPoints.map((pd) => {
                    const items = formatPublicData(pd.result_data);
                    const pdKey = pd.query_type || items[0]?.key || '';
                    return items.map((item, i) => (
                      <div key={pd.id + '-' + i} className="flex justify-between gap-4 py-1 border-b border-slate-50 last:border-0">
                        <span className="text-xs font-medium text-slate-500 shrink-0 whitespace-nowrap">
                          {prettyKey(item.key === 'value' ? pdKey : (item.key || pdKey))}
                        </span>
                        <div className="text-right">
                          <span className="text-xs text-slate-700">{renderValue(item.value, item.key === 'value' ? pdKey : (item.key || pdKey))}</span>
                        </div>
                      </div>
                    ));
                  })}
                  {dataPoints.length === 0 && <p className="text-xs text-slate-400">No data points returned.</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {history.length > 0 && (
        <div className="mt-2">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="text-sm font-medium text-slate-500 hover:text-slate-700 inline-flex items-center gap-2"
          >
            <History size={16} />
            Search History ({history.length} {history.length === 1 ? 'run' : 'runs'})
            {showHistory ? ' - hide' : ' - show'}
          </button>
          {showHistory && (
            <div className="mt-3 space-y-2">
              {history.map((run, i) => (
                <div key={i} className="p-3 rounded-lg border border-slate-200 bg-white">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-medium text-slate-600">{new Date(run.timestamp).toLocaleString()}</span>
                    <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-500">
                      {run.result_count} results from {run.source_count} sources
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {(run.sources || []).map((s) => (
                      <span key={s} className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{s}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}